import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  getAutopilotJobs,
  scanCandidatesForJob,
  sendBulkEmails,
  previewAutopilotEmail,
  getAutopilotStatus
} from '../services/api';
import CampaignList from '../components/autopilot/CampaignList';
import CampaignForm from '../components/autopilot/CampaignForm';
import CampaignDetail from '../components/autopilot/CampaignDetail';
import ApprovalQueue from '../components/autopilot/ApprovalQueue';

function Autopilot() {
  // View state
  const [activeView, setActiveView] = useState('campaigns'); // campaigns, quick-send, queue
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);

  // Quick Send state (legacy)
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [matches, setMatches] = useState([]);
  const [belowThreshold, setBelowThreshold] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [threshold, setThreshold] = useState(80);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [sendResults, setSendResults] = useState(null);
  const [autopilotStatus, setAutopilotStatus] = useState(null);

  // Fetch jobs and status on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobsRes, statusRes] = await Promise.all([
          getAutopilotJobs(),
          getAutopilotStatus()
        ]);
        setJobs(jobsRes.data.jobs || []);
        setAutopilotStatus(statusRes.data);
      } catch (error) {
        console.error('Error fetching autopilot data:', error);
      }
    };
    fetchData();
  }, []);

  // Quick Send handlers
  const handleJobSelect = (job) => {
    setSelectedJob(job);
    setMatches([]);
    setBelowThreshold([]);
    setSelectedCandidates([]);
    setScanResult(null);
    setPreviewEmail(null);
    setSendResults(null);
  };

  const handleScan = async () => {
    if (!selectedJob) {
      toast.error('Please select a job first');
      return;
    }

    setIsLoading(true);
    setLoadingAction('scanning');
    setMatches([]);
    setBelowThreshold([]);
    setSelectedCandidates([]);
    setSendResults(null);

    try {
      const response = await scanCandidatesForJob(selectedJob._id, {
        threshold,
        limit: 100
      });

      const data = response.data;
      setMatches(data.matches || []);
      setBelowThreshold(data.belowThreshold || []);
      setScanResult({
        totalScanned: data.totalScanned,
        qualifiedCount: data.qualifiedCount,
        threshold: data.threshold
      });

      if (data.qualifiedCount > 0) {
        toast.success(`Found ${data.qualifiedCount} qualified candidates!`);
      } else {
        toast.info('No candidates meet the threshold. Try lowering it.');
      }
    } catch (error) {
      console.error('Error scanning candidates:', error);
      toast.error('Failed to scan candidates');
    } finally {
      setIsLoading(false);
      setLoadingAction('');
    }
  };

  const toggleCandidateSelection = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      }
      return [...prev, candidateId];
    });
  };

  const selectAllCandidates = () => {
    const allIds = matches.map(m => m.candidate._id);
    setSelectedCandidates(allIds);
  };

  const deselectAllCandidates = () => {
    setSelectedCandidates([]);
  };

  const handlePreviewEmail = async (match) => {
    setIsLoading(true);
    setLoadingAction('preview');

    try {
      const response = await previewAutopilotEmail({
        candidateId: match.candidate._id,
        jobId: selectedJob._id,
        matchScore: match.matchScore
      });

      setPreviewEmail(response.data);
    } catch (error) {
      console.error('Error previewing email:', error);
      toast.error('Failed to generate preview');
    } finally {
      setIsLoading(false);
      setLoadingAction('');
    }
  };

  const handleSendEmails = async () => {
    if (selectedCandidates.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }

    const confirmSend = window.confirm(
      `Are you sure you want to send emails to ${selectedCandidates.length} candidate(s)?`
    );

    if (!confirmSend) return;

    setIsLoading(true);
    setLoadingAction('sending');
    setSendResults(null);

    try {
      const candidateData = selectedCandidates.map(candidateId => {
        const match = matches.find(m => m.candidate._id === candidateId);
        return {
          candidateId,
          matchScore: match?.matchScore || 80
        };
      });

      const response = await sendBulkEmails({
        jobId: selectedJob._id,
        candidates: candidateData
      });

      const data = response.data;
      setSendResults(data);

      if (data.successCount > 0) {
        toast.success(`Successfully sent ${data.successCount} email(s)!`);
      }

      if (data.failedCount > 0) {
        toast.warn(`Failed to send ${data.failedCount} email(s)`);
      }

      const successIds = data.results
        .filter(r => r.success)
        .map(r => r.candidateId);
      setSelectedCandidates(prev => prev.filter(id => !successIds.includes(id)));

    } catch (error) {
      console.error('Error sending emails:', error);
      toast.error('Failed to send emails');
    } finally {
      setIsLoading(false);
      setLoadingAction('');
    }
  };

  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'bg-green-100 text-green-800 border-green-300';
    if (score >= 80) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (score >= 70) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (score >= 50) return 'bg-orange-100 text-orange-800 border-orange-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  // Campaign handlers
  const handleSelectCampaign = (campaign) => {
    setSelectedCampaign(campaign);
  };

  const handleCreateCampaign = () => {
    setEditingCampaign(null);
    setShowCampaignForm(true);
  };

  const handleEditCampaign = () => {
    setEditingCampaign(selectedCampaign);
    setShowCampaignForm(true);
  };

  const handleCampaignSaved = () => {
    setShowCampaignForm(false);
    setEditingCampaign(null);
    setSelectedCampaign(null);
  };

  // Render Campaign Form Modal
  if (showCampaignForm) {
    return (
      <div className="container mx-auto px-4 py-8">
        <CampaignForm
          campaign={editingCampaign}
          onSave={handleCampaignSaved}
          onCancel={() => {
            setShowCampaignForm(false);
            setEditingCampaign(null);
          }}
        />
      </div>
    );
  }

  // Render Campaign Detail
  if (selectedCampaign) {
    return (
      <div className="container mx-auto px-4 py-8">
        <CampaignDetail
          campaign={selectedCampaign}
          onBack={() => setSelectedCampaign(null)}
          onEdit={handleEditCampaign}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Autopilot</h1>
            <p className="text-sm text-gray-500">Automated candidate sourcing and outreach</p>
          </div>
        </div>
        {autopilotStatus && (
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              autopilotStatus.status?.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {autopilotStatus.status?.enabled ? 'Active' : 'Inactive'}
            </span>
          </div>
        )}
      </div>

      {/* Main Tabs */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex gap-1 px-4">
            <button
              onClick={() => setActiveView('campaigns')}
              className={`py-4 px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeView === 'campaigns'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Campaigns
            </button>
            <button
              onClick={() => setActiveView('queue')}
              className={`py-4 px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeView === 'queue'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Approval Queue
            </button>
            <button
              onClick={() => setActiveView('quick-send')}
              className={`py-4 px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeView === 'quick-send'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Quick Send
            </button>
          </nav>
        </div>
      </div>

      {/* Campaigns View */}
      {activeView === 'campaigns' && (
        <CampaignList
          onSelectCampaign={handleSelectCampaign}
          onCreateCampaign={handleCreateCampaign}
        />
      )}

      {/* Approval Queue View */}
      {activeView === 'queue' && (
        <ApprovalQueue />
      )}

      {/* Quick Send View (Legacy) */}
      {activeView === 'quick-send' && (
        <div className="space-y-6">
          {/* Step 1: Select Job */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm font-semibold">1</span>
                <h2 className="text-lg font-semibold">Select Job</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1 ml-8">Choose a job to match candidates against</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {jobs.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    No open jobs found. Create a job first.
                  </div>
                ) : (
                  jobs.map((job) => (
                    <div
                      key={job._id}
                      onClick={() => handleJobSelect(job)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedJob?._id === job._id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                      }`}
                    >
                      <h3 className="font-semibold text-gray-900">{job.title}</h3>
                      <p className="text-sm text-gray-600">{job.company || 'Company'}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                        <span>{job.location || 'Remote'}</span>
                        {job.industry && (
                          <>
                            <span>•</span>
                            <span>{job.industry}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Step 2: Configure & Scan */}
          {selectedJob && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm font-semibold">2</span>
                  <h2 className="text-lg font-semibold">Scan Candidates</h2>
                </div>
                <p className="text-sm text-gray-500 mt-1 ml-8">Configure threshold and scan talent pool</p>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Match Threshold
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="50"
                        max="95"
                        step="5"
                        value={threshold}
                        onChange={(e) => setThreshold(parseInt(e.target.value))}
                        className="w-32"
                      />
                      <span className="text-lg font-semibold text-indigo-600">{threshold}%</span>
                    </div>
                  </div>
                  <button
                    onClick={handleScan}
                    disabled={isLoading}
                    className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isLoading && loadingAction === 'scanning' ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Scanning...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Scan Candidates
                      </>
                    )}
                  </button>
                </div>

                {scanResult && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-6 text-sm">
                      <div>
                        <span className="text-gray-500">Scanned:</span>
                        <span className="ml-1 font-semibold">{scanResult.totalScanned}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Qualified (≥{scanResult.threshold}%):</span>
                        <span className="ml-1 font-semibold text-green-600">{scanResult.qualifiedCount}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Below threshold:</span>
                        <span className="ml-1 font-semibold text-orange-600">{belowThreshold.length}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Review & Select Candidates */}
          {matches.length > 0 && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm font-semibold">3</span>
                    <h2 className="text-lg font-semibold">Select Candidates</h2>
                    <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-sm">
                      {matches.length} matches
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAllCandidates}
                      className="text-sm text-indigo-600 hover:text-indigo-700"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={deselectAllCandidates}
                      className="text-sm text-gray-600 hover:text-gray-700"
                    >
                      Clear
                    </button>
                    {selectedCandidates.length > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                        {selectedCandidates.length} selected
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
                {matches.map((match) => (
                  <div
                    key={match.candidate._id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      selectedCandidates.includes(match.candidate._id) ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        checked={selectedCandidates.includes(match.candidate._id)}
                        onChange={() => toggleCandidateSelection(match.candidate._id)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-gray-900">{match.candidate.fullName}</h3>
                            <p className="text-sm text-gray-600">{match.candidate.email}</p>
                          </div>
                          <div className={`px-3 py-1 rounded-full border text-sm font-semibold ${getMatchScoreColor(match.matchScore)}`}>
                            {match.matchScore}% Match
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {match.candidate.domain && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                              {match.candidate.domain}
                            </span>
                          )}
                          {match.candidate.location && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                              {match.candidate.location}
                            </span>
                          )}
                          {match.candidate.skills?.slice(0, 4).map((skill, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">
                              {skill}
                            </span>
                          ))}
                        </div>
                        {match.matchSummary && match.matchSummary.length > 0 && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Why they match: </span>
                            {match.matchSummary.slice(0, 2).join(' • ')}
                          </div>
                        )}
                        <div className="mt-2">
                          <button
                            onClick={() => handlePreviewEmail(match)}
                            disabled={isLoading && loadingAction === 'preview'}
                            className="text-sm text-indigo-600 hover:text-indigo-700"
                          >
                            Preview Email
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Below Threshold Section */}
          {belowThreshold.length > 0 && (
            <details className="bg-white shadow rounded-lg">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50">
                <span className="text-sm font-medium text-gray-600">
                  Show {belowThreshold.length} candidates below {threshold}% threshold
                </span>
              </summary>
              <div className="px-6 pb-4 divide-y divide-gray-100">
                {belowThreshold.map((match) => (
                  <div key={match.candidate._id} className="py-3 flex items-center justify-between">
                    <div>
                      <span className="font-medium text-gray-700">{match.candidate.fullName}</span>
                      <span className="ml-2 text-sm text-gray-500">{match.candidate.domain}</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-sm ${getMatchScoreColor(match.matchScore)}`}>
                      {match.matchScore}%
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Step 4: Send Emails */}
          {selectedCandidates.length > 0 && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm font-semibold">4</span>
                  <h2 className="text-lg font-semibold">Send Outreach</h2>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600">
                      Ready to send personalized emails to <strong>{selectedCandidates.length}</strong> candidate(s)
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Each candidate will receive a unique AI-generated message based on their profile
                    </p>
                  </div>
                  <button
                    onClick={handleSendEmails}
                    disabled={isLoading}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isLoading && loadingAction === 'sending' ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Send {selectedCandidates.length} Email{selectedCandidates.length > 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Send Results */}
          {sendResults && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Send Results
                </h2>
              </div>
              <div className="p-6">
                <div className="flex gap-6 mb-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{sendResults.successCount}</div>
                    <div className="text-sm text-gray-500">Sent</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600">{sendResults.failedCount}</div>
                    <div className="text-sm text-gray-500">Failed</div>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {sendResults.results.map((result, idx) => (
                    <div key={idx} className="py-2 flex items-center justify-between">
                      <div>
                        <span className="font-medium">{result.candidateName}</span>
                        <span className="ml-2 text-sm text-gray-500">{result.email}</span>
                      </div>
                      {result.success ? (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Sent
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-600 text-sm">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          {result.error || 'Failed'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!selectedJob && jobs.length > 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Quick Send Mode</h3>
              <p className="mt-1 text-sm text-gray-500">
                Select a job above to scan and send emails to candidates
              </p>
            </div>
          )}
        </div>
      )}

      {/* Email Preview Modal */}
      {previewEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Email Preview</h3>
              <button
                onClick={() => setPreviewEmail(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase">To</label>
                  <p className="font-medium">{previewEmail.candidate.fullName} &lt;{previewEmail.email.to}&gt;</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">From</label>
                  <p className="font-medium">{previewEmail.email.from}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Subject</label>
                  <p className="font-medium">{previewEmail.email.subject}</p>
                </div>
                <div className="pt-4 border-t">
                  <label className="text-xs text-gray-500 uppercase">Message</label>
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg whitespace-pre-wrap text-gray-700">
                    {previewEmail.email.body}
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setPreviewEmail(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Autopilot;
